/**
 * FR-100 — Trends: what was paid out of pocket, over time.
 *
 * One chart answers the questions, with two switches on it: amount or count,
 * and what to split the bars by (type, place, person). The list under the
 * chart is its legend and its ranking at once. A second, smaller chart shows
 * the number Home leads with — ready to claim — as it grew. The last card is
 * the account itself: its balance, and how much of it is growth (TR-70).
 *
 * There is nothing to load that the Expenses page does not already load, and
 * nothing to store: see trends.ts. The period and the switches live in the
 * address, so the back button returns to the view that was left, which matters
 * because every bar and row leads away to the records behind it.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks.js';
import { Balance, Empty, ErrorBox, Icon, PageHead, Skeleton, formatMoney } from '../ui.js';
import { BarChart, LineChart, seriesColor } from '../charts.js';
import { HsaCard } from './HsaCard.js';
import {
  MONTH_NAMES,
  balance,
  monthIndex,
  periodIds,
  periodOf,
  prepare,
  scopeQuery,
  seriesOf,
  stacks,
  yearOf,
  type Measure,
  type Period,
  type Scope,
  type SplitBy,
} from '../trends.js';
import type { CoveredPerson, Expense, Reimbursement } from '../types.js';

const SPLITS: { id: SplitBy; label: string }[] = [
  { id: 'total', label: 'Nothing' },
  { id: 'type', label: 'Type' },
  { id: 'place', label: 'Place' },
  { id: 'person', label: 'Person' },
];

const plural = (n: number, word: string): string => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

export function Trends() {
  const all = useApi<{ items: Expense[] }>('/api/expenses?status=all');
  const persons = useApi<{ persons: CoveredPerson[] }>('/api/persons');
  // When a withdrawal was matched is on its reimbursement, not on the expense.
  // Asked for only once something has been paid back.
  const anyPaidBack = (all.data?.items ?? []).some((e) => e.status === 'reimbursed');
  const paid = useApi<{ reimbursements: Reimbursement[] }>(anyPaidBack ? '/api/reimbursements' : null);

  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const now = useMemo(() => monthIndex(new Date().getFullYear(), new Date().getMonth() + 1), []);

  const { entries, waiting, places } = useMemo(
    () => prepare(all.data?.items ?? [], paid.data?.reimbursements ?? []),
    [all.data, paid.data],
  );
  const people = useMemo(() => persons.data?.persons ?? [], [persons.data]);

  const ids = useMemo(() => periodIds(entries, now), [entries, now]);
  const asked = params.get('period') ?? '';
  const period = useMemo(() => periodOf(ids.includes(asked) ? asked : 'last12', entries, now), [ids, asked, entries, now]);
  const measure: Measure = params.get('show') === 'count' ? 'count' : 'amount';
  const by: SplitBy = SPLITS.find((s) => s.id === params.get('split'))?.id ?? 'type';

  /** Change one choice, keep the others, and leave the defaults out of the address. */
  const choose = (key: 'period' | 'show' | 'split', value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => entries.filter((e) => e.month >= period.from && e.month <= period.to), [entries, period]);
  const split = useMemo(() => seriesOf(by, rows, measure, { people, places }), [by, rows, measure, people, places]);
  const data = useMemo(() => stacks(period, split, rows), [period, split, rows]);
  const ready = useMemo(() => balance(period, entries), [period, entries]);

  if (all.error ?? persons.error) return <ErrorBox error={all.error ?? persons.error} />;
  // Wait for the names too. Drawn before the family list arrives, the person
  // split would call everyone "Someone else"; before the reimbursements, the
  // balance would be drawn too high and then drop.
  if (!all.data || !persons.data || (anyPaidBack && !paid.data && !paid.error)) {
    return (
      <>
        <PageHead title="Trends" />
        <Skeleton rows={4} />
      </>
    );
  }
  if (entries.length === 0) {
    return (
      <>
        <PageHead title="Trends">What you paid out of pocket, by the date of the care or purchase.</PageHead>
        <Empty>
          <p>Nothing is filed yet. This fills in as expenses are filed.</p>
          <Link className="btn primary" to={waiting > 0 ? '/expenses?status=draft' : '/add'}>
            {waiting > 0 ? null : <Icon name="plus" />}
            {waiting > 0 ? `Review ${plural(waiting, 'expense')}` : 'Add a receipt'}
          </Link>
        </Empty>
        <HsaCard ready={0} />
      </>
    );
  }

  const total = rows.reduce((t, e) => t + e.cents, 0);
  // Today's figure, whatever period is chosen: filed and not paid back.
  const readyNow = entries.reduce((t, e) => t + (e.paidBack === null ? e.cents : 0), 0);
  const amount = measure === 'amount';
  const last = period.buckets[period.buckets.length - 1];
  const within = (scope: Partial<Scope>): string => `/expenses?${scopeQuery({ from: period.from, to: period.to, ...scope })}`;
  const only = (key: string): Partial<Scope> => (by === 'type' ? { type: key } : by === 'place' ? { place: key } : { person: key });

  return (
    <>
      <PageHead title="Trends">What you paid out of pocket, by the date of the care or purchase.</PageHead>

      <div className="chips scroll" role="group" aria-label="Period">
        {ids.map((id) => (
          <button key={id} className="chip" aria-pressed={period.id === id} onClick={() => choose('period', id, 'last12')}>
            {id === 'last12' ? 'Last 12 months' : id === 'all' ? 'All time' : id}
          </button>
        ))}
      </div>

      <section className="hero" aria-label="Summary">
        <div className="label">Paid out of pocket {phrase(period)}</div>
        <Balance cents={total} />
        <div className="meta">
          {plural(rows.length, 'expense')} · {formatMoney(Math.round(total / period.months))} a month on average
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Each {period.unit}</h2>
            <div className="sub">
              {amount ? 'Amount paid' : 'Number of expenses'}
              {last?.partial ? `. The lighter bar is the ${period.unit} in progress.` : ''}
            </div>
          </div>
          <div className="picks">
            <div className="pick">
              <span className="pick-label" id="trends-show">
                Show
              </span>
              <div className="switch" role="group" aria-labelledby="trends-show">
                <button aria-pressed={amount} onClick={() => choose('show', 'amount', 'amount')}>
                  Amount
                </button>
                <button aria-pressed={!amount} onClick={() => choose('show', 'count', 'amount')}>
                  Count
                </button>
              </div>
            </div>
            <div className="pick">
              <span className="pick-label" id="trends-split">
                Split by
              </span>
              <div className="switch" role="group" aria-labelledby="trends-split">
                {SPLITS.map((s) => (
                  <button key={s.id} aria-pressed={by === s.id} onClick={() => choose('split', s.id, 'type')}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {rows.length === 0 ? (
          <Empty>
            <p>Nothing was filed {phrase(period)}.</p>
          </Empty>
        ) : (
          <>
            <BarChart
              period={period}
              split={split}
              data={data}
              measure={measure}
              label={`${amount ? 'Amount paid' : 'Number of expenses'} each ${period.unit}, ${phrase(period)}`}
              onPick={(k) => {
                const b = period.buckets[k]!;
                navigate(within({ from: Math.max(b.from, period.from), to: Math.min(b.to, period.to) }));
              }}
            />
            {split.list.length > 1 ? (
              <div className="breakdown">
                {split.list.map((s) => {
                  const biggest = Math.max(...split.list.map((x) => (amount ? x.cents : x.count)));
                  const cells = (
                    <>
                      <i className="swatch" style={{ background: seriesColor(s.slot) }} />
                      <span className="bname">
                        {s.name}
                        {s.note ? <small> · {s.note}</small> : null}
                      </span>
                      <span className="btrack">
                        <span
                          className="bfill"
                          style={{
                            width: `${Math.max(1.5, ((amount ? s.cents : s.count) / biggest) * 100)}%`,
                            background: seriesColor(s.slot),
                          }}
                        />
                      </span>
                      <span className="bval">{amount ? formatMoney(s.cents) : s.count.toLocaleString('en-US')}</span>
                      <span className="bsub">{amount ? plural(s.count, 'expense') : formatMoney(s.cents)}</span>
                    </>
                  );
                  // The grouped tail is many places, so it has no one list to open.
                  // Keyed by what a row is, not what it is called: two people can share a name.
                  return s.key === null ? (
                    <div className="brow" key="rest">
                      {cells}
                    </div>
                  ) : (
                    <Link className="brow" key={`is:${s.key}`} to={within(only(s.key))}>
                      {cells}
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Ready to claim</h2>
            <div className="sub">Filed and not yet paid back by the HSA, at the end of each {period.unit}</div>
          </div>
        </div>
        {paid.error ? (
          <ErrorBox error={paid.error} />
        ) : (
          <LineChart period={period} points={ready} label={`Ready to claim at the end of each ${period.unit}, ${phrase(period)}`} />
        )}
      </section>

      <HsaCard ready={readyNow} />

      {waiting > 0 ? (
        <p className="small muted">
          {waiting === 1 ? '1 expense is' : `${waiting} expenses are`} waiting for review and {waiting === 1 ? 'is' : 'are'} not
          counted yet. <Link to="/expenses?status=draft">Review {waiting === 1 ? 'it' : 'them'}</Link>
        </p>
      ) : null}
    </>
  );
}

/** "in the last 12 months", "in 2025", "since Feb 2023". */
function phrase(period: Period): string {
  if (period.id === 'last12') return 'in the last 12 months';
  if (period.id === 'all') return `since ${MONTH_NAMES[period.from % 12]} ${yearOf(period.from)}`;
  return `in ${period.id}`;
}
